import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Lock, MapPin, Camera, Smartphone, Building2, Database,
  ScrollText, FileCheck, ArrowLeft, Users, Cloud, AlertTriangle, Mail,
  Globe,
} from "lucide-react";

/**
 * Página pública de Termos de Uso e Política de Privacidade (LGPD).
 * Rota: /termos — acessível sem autenticação.
 * fev/2026 — atende requisito de transparência LGPD para SaaS multi-tenant.
 */

const HOJE = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className="p-2 rounded-lg bg-orange-100 text-orange-700 shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
      </div>
    </div>
    <div className="pl-11 text-slate-700 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Item = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-0">
    <div className="w-full sm:w-56 shrink-0 text-sm font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
    <div className="flex-1 text-sm">{children}</div>
  </div>
);

export default function Termos() {
  const navigate = useNavigate();
  // Detecta se usuário está autenticado — a página é PÚBLICA em qualquer caso,
  // mas os CTAs de "Voltar" apontam pra lugares diferentes: sem sessão → /login,
  // com sessão → -1 (voltar na history) ou dashboard.
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const backLabel = isAuthenticated ? "Voltar" : "Voltar para Login";
  const goBack = () => {
    if (isAuthenticated) navigate(-1);
    else navigate("/login", { replace: false });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/40 to-white" data-testid="page-termos">
      {/* Cabeçalho */}
      <header className="border-b border-orange-100 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-orange-600 text-white">
              <ScrollText className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">Termos de Uso & LGPD</div>
              <div className="text-xs text-slate-500">Gestão de Entregas — usegestao.app.br</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={goBack} data-testid="btn-voltar-termos">
            <ArrowLeft className="w-4 h-4 mr-1.5" />{backLabel}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Aviso: página é 100% pública (fev/2026 UX fix — usuário achava que precisava logar) */}
        {!isAuthenticated && (
          <div
            className="mb-6 flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50/70"
            data-testid="termos-public-notice"
          >
            <div className="p-1.5 rounded-lg bg-emerald-600 text-white shrink-0">
              <Globe className="w-4 h-4" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-emerald-900">Página pública — não requer login</div>
              <div className="text-xs text-emerald-800 mt-0.5">
                Este documento fica disponível pra qualquer pessoa consultar. Ao clicar em <b>Voltar para Login</b> você retorna à tela de acesso e pode continuar seu login normalmente.
              </div>
            </div>
          </div>
        )}
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="bg-orange-100 text-orange-800 border-orange-200 mb-3">
            <FileCheck className="w-3 h-3 mr-1" />Atualizado em {HOJE}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-2">
            Termos de Uso e Política de Privacidade
          </h1>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto">
            Documento único que descreve como o sistema <b>Gestão de Entregas</b> coleta, usa e protege
            os dados das empresas que o utilizam, em conformidade com a{" "}
            <b>Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018)</b>.
          </p>
        </div>

        {/* Sumário */}
        <Card className="mb-8 border-orange-200 bg-orange-50/50">
          <CardContent className="pt-5">
            <div className="text-xs font-bold text-slate-600 uppercase mb-2">Sumário rápido</div>
            <ul className="grid md:grid-cols-2 gap-1.5 text-sm">
              <li><a href="#finalidade" className="text-orange-700 hover:underline">1. Finalidade da coleta de dados</a></li>
              <li><a href="#isolamento" className="text-orange-700 hover:underline">2. Isolamento entre empresas</a></li>
              <li><a href="#responsabilidade" className="text-orange-700 hover:underline">3. Responsabilidades</a></li>
              <li><a href="#armazenamento" className="text-orange-700 hover:underline">4. Armazenamento e backups</a></li>
              <li><a href="#direitos" className="text-orange-700 hover:underline">5. Direitos do titular (LGPD)</a></li>
              <li><a href="#uso" className="text-orange-700 hover:underline">6. Uso aceitável do sistema</a></li>
              <li><a href="#seguranca" className="text-orange-700 hover:underline">7. Segurança da informação</a></li>
              <li><a href="#alteracoes" className="text-orange-700 hover:underline">8. Alterações destes termos</a></li>
              <li><a href="#contato" className="text-orange-700 hover:underline">9. Contato</a></li>
            </ul>
          </CardContent>
        </Card>

        <div className="space-y-10">
          {/* 1. FINALIDADE */}
          <Section id="finalidade" icon={FileCheck} title="1. Finalidade da coleta de dados">
            <p>
              A plataforma <b>Gestão de Entregas</b> é uma ferramenta de gestão logística
              utilizada por farmácias, distribuidoras e empresas de delivery para
              administrar suas próprias operações. Coletamos apenas os dados
              <b> estritamente necessários</b> para comprovar a conclusão da entrega
              e permitir a operação diária do negócio.
            </p>
            <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
              <div className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                Dados coletados na tela do entregador (mobile)
              </div>
              <Item label={<><MapPin className="w-3.5 h-3.5 inline mr-1"/>Localização GPS</>}>
                Coordenadas de latitude e longitude capturadas <b>no momento exato</b> da confirmação
                da entrega. Servem apenas como prova geográfica de que a entrega ocorreu no local
                combinado. <b>Não</b> monitoramos a rota do entregador em tempo real fora do momento
                da confirmação, nem armazenamos histórico contínuo de deslocamento.
              </Item>
              <Item label={<><Camera className="w-3.5 h-3.5 inline mr-1"/>Foto da evidência</>}>
                Uma imagem opcional (canhoto, receita, produto entregue) tirada pelo entregador
                para comprovar a entrega. Fica visível apenas para a equipe da empresa contratante
                do sistema.
              </Item>
              <Item label={<><Smartphone className="w-3.5 h-3.5 inline mr-1"/>4 últimos dígitos do celular do cliente</>}>
                Utilizados exclusivamente como <b>segundo fator</b> de confirmação da entrega
                (o entregador digita, o cliente confirma). Nunca compartilhamos o número completo
                do cliente com terceiros. Se a empresa optar pela verificação por SMS, o número
                completo é usado apenas para enviar o código de 4 dígitos e é imediatamente
                descartado após o envio.
              </Item>
            </div>
            <p className="text-sm">
              Esses dados são vinculados à empresa contratante e à entrega específica.
              Não são utilizados para publicidade, perfilamento ou qualquer outro fim não relacionado
              à comprovação operacional.
            </p>
          </Section>

          {/* 2. ISOLAMENTO */}
          <Section id="isolamento" icon={Building2} title="2. Isolamento entre empresas (multi-tenant)">
            <p>
              A arquitetura do <b>Gestão de Entregas</b> é <b>multi-tenant estrita</b>. Isso
              significa que cada empresa cadastrada possui um identificador único (
              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">empresa_id</code>) e todas
              as consultas ao banco de dados são <b>obrigatoriamente escopadas</b> por esse
              identificador antes de retornar qualquer registro.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />Garantias técnicas
              </div>
              <ul className="text-sm space-y-1.5 list-disc pl-5">
                <li>Entregas, entregadores, bairros, regiões, tipos de entrega, formas de pagamento e usuários são armazenados em coleções separadas por <code className="text-xs bg-white px-1 rounded">empresa_id</code>.</li>
                <li>Nenhuma consulta ao banco retorna dados sem o filtro de tenant aplicado no backend — controle centralizado na camada de API, não nas telas.</li>
                <li>Backups são gerados <b>por empresa</b> — cada arquivo <code className="text-xs bg-white px-1 rounded">backup_&lt;slug&gt;_&lt;data&gt;.zip</code> contém somente dados daquela empresa. Restauração afeta apenas o tenant de origem.</li>
                <li>Super administradores da plataforma possuem acesso apenas para suporte técnico, com todas as ações registradas em auditoria imutável.</li>
              </ul>
            </div>
            <p className="text-sm">
              Em outras palavras: <b>uma empresa nunca consegue ver, listar, exportar ou modificar
              dados de outra empresa</b> — nem por engano, nem intencionalmente através da interface.
            </p>
          </Section>

          {/* 3. RESPONSABILIDADE */}
          <Section id="responsabilidade" icon={Users} title="3. Responsabilidades">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-lg p-4 bg-white">
                <div className="text-sm font-bold text-slate-700 mb-2">Papel da plataforma</div>
                <p className="text-sm">
                  Somos <b>operadores</b> dos dados conforme LGPD Art. 5º, VII. Fornecemos a
                  ferramenta de gestão e agimos apenas sob instrução da empresa contratante,
                  que é a controladora dos dados dos seus clientes.
                </p>
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-white">
                <div className="text-sm font-bold text-slate-700 mb-2">Papel da empresa contratante</div>
                <p className="text-sm">
                  A empresa é <b>controladora</b> dos dados que insere no sistema. Cabe a ela
                  informar seus próprios clientes sobre a coleta de dados de entrega (fotos, GPS,
                  telefone), obter as bases legais adequadas e responder a solicitações LGPD dos
                  titulares.
                </p>
              </div>
            </div>
            <p className="text-sm mt-3">
              O sistema é uma <b>ferramenta de gestão</b> — não substitui as obrigações da empresa
              contratante quanto à LGPD, sanitária, fiscal ou regulatória. A empresa é a única
              responsável pelo teor comercial das entregas registradas, pela veracidade dos
              cadastros e pela conduta de seus entregadores.
            </p>
          </Section>

          {/* 4. ARMAZENAMENTO */}
          <Section id="armazenamento" icon={Database} title="4. Armazenamento e backups">
            <p>
              Todos os dados são armazenados em <b>servidores seguros</b> em infraestrutura
              gerenciada, com criptografia em trânsito (HTTPS/TLS 1.2+) e controle de acesso
              autenticado.
            </p>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Cloud className="w-4 h-4 text-blue-600" />Política de backup
              </div>
              <ul className="text-sm space-y-1.5 list-disc pl-5">
                <li>Backups <b>diários automatizados</b> em horário configurável pelo Super Administrador (por padrão, madrugada BRT).</li>
                <li>Cada backup é isolado por empresa e contém apenas os dados daquela empresa.</li>
                <li>Retenção padrão de <b>30 dias</b>, ajustável entre 1 e 365 dias pelo Super Administrador. Um backup por empresa é mantido como <b>safety net</b> mesmo se toda a janela de retenção expirar.</li>
                <li>Todo backup gerado, restaurado ou apagado é registrado em auditoria e no <b>Histórico de Retenção</b>, com rastreabilidade completa (quem, quando, por qual motivo).</li>
                <li>Backups podem ser baixados apenas por usuários com permissão explícita <code className="text-xs bg-slate-100 px-1 rounded">backup.v</code> na matriz.</li>
              </ul>
            </div>
          </Section>

          {/* 5. DIREITOS LGPD */}
          <Section id="direitos" icon={Shield} title="5. Direitos do titular (LGPD Art. 18)">
            <p>Todo titular de dados tem direito a exercer, junto à empresa controladora:</p>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              {[
                ["Confirmação da existência de tratamento", "Saber se seus dados estão sendo utilizados"],
                ["Acesso", "Solicitar cópia dos dados coletados"],
                ["Correção", "Atualizar dados incompletos, inexatos ou desatualizados"],
                ["Anonimização ou eliminação", "Pedir remoção de dados desnecessários ou tratados em desconformidade"],
                ["Portabilidade", "Exportar seus dados para outro serviço"],
                ["Informação sobre compartilhamento", "Saber com quem os dados foram compartilhados"],
                ["Revogação de consentimento", "Retirar autorização quando aplicável"],
                ["Oposição", "Contestar tratamento realizado com base em legítimo interesse"],
              ].map(([titulo, desc]) => (
                <div key={titulo} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                  <FileCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <b>{titulo}</b>
                    <div className="text-xs text-slate-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm mt-3">
              Solicitações devem ser dirigidas à empresa contratante do sistema (controladora
              dos dados). A plataforma atua exclusivamente como operadora e apoia a controladora
              no atendimento a essas solicitações quando formalmente demandada.
            </p>
          </Section>

          {/* 6. USO ACEITÁVEL */}
          <Section id="uso" icon={AlertTriangle} title="6. Uso aceitável do sistema">
            <p>Ao utilizar o Gestão de Entregas, o usuário concorda em:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li>Utilizar o sistema apenas para fins legítimos de gestão logística da própria empresa.</li>
              <li>Não tentar acessar dados de outras empresas por qualquer meio (exploração, engenharia reversa, tentativa de bypass da segmentação multi-tenant).</li>
              <li>Manter em sigilo suas credenciais de acesso — cada usuário é responsável pelas ações realizadas com sua conta.</li>
              <li>Não inserir dados de terceiros sem base legal adequada (consentimento, execução de contrato ou legítimo interesse).</li>
              <li>Não utilizar o sistema para atividades ilegais, práticas concorrenciais desleais ou qualquer atividade que viole a legislação brasileira.</li>
            </ul>
            <p className="text-sm">
              O descumprimento pode levar à <b>suspensão imediata do acesso</b>, sem prejuízo das
              medidas legais cabíveis.
            </p>
          </Section>

          {/* 7. SEGURANÇA */}
          <Section id="seguranca" icon={Lock} title="7. Segurança da informação">
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li><b>Autenticação:</b> senhas armazenadas apenas como hash bcrypt (não recuperáveis). Suporte a segundo fator (TOTP) para Super Administradores.</li>
              <li><b>Sessão:</b> tokens HTTP-only cookies com escopo estrito. Não expostos a JavaScript da página.</li>
              <li><b>Trânsito:</b> HTTPS obrigatório em todas as chamadas de API e assets. Certificado TLS renovado automaticamente.</li>
              <li><b>Controle de acesso:</b> matriz granular de permissões (V/I/A/E) por recurso — o admin de cada empresa define o que cada usuário pode ver, incluir, alterar e excluir.</li>
              <li><b>Auditoria:</b> ações sensíveis (login, criação/edição/exclusão, restauração de backup) são registradas em log imutável com timestamp UTC + identidade do usuário.</li>
            </ul>
          </Section>

          {/* 8. ALTERAÇÕES */}
          <Section id="alteracoes" icon={ScrollText} title="8. Alterações destes termos">
            <p>
              A plataforma pode atualizar este documento para refletir mudanças legais,
              técnicas ou de negócio. Alterações materiais são anunciadas com pelo menos
              <b> 15 dias de antecedência</b> aos administradores cadastrados. A data
              da última atualização aparece no topo desta página.
            </p>
          </Section>

          {/* 9. CONTATO */}
          <Section id="contato" icon={Mail} title="9. Contato">
            <p>
              Dúvidas sobre estes termos, exercício de direitos LGPD ou reporte de
              incidentes de segurança devem ser encaminhados ao administrador da empresa
              contratante — que é a controladora dos dados. Para questões sobre o
              funcionamento técnico da plataforma, entre em contato através do canal de
              suporte fornecido pela sua empresa.
            </p>
          </Section>
        </div>

        {/* Rodapé da página */}
        <div className="mt-12 pt-6 border-t border-slate-200 text-center text-xs text-slate-500 space-y-1">
          <div>Gestão de Entregas — plataforma de gestão logística multi-tenant.</div>
          <div>Este documento é público e permanentemente disponível em <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">/termos</code>.</div>
          <div className="pt-2">
            <Link to="/" className="text-orange-700 hover:underline">← Voltar para o início</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
